import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { Shield, Trophy } from 'lucide-react';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import React from 'react';
import type { MessageDescriptor } from 'shared/i18n';
import { Decor, Hot, Logo, Poster, PosterHeader, SchemeTable, Subtitle, Title, Titles, useKeyText } from './scheme-parts';

/** Column headings, the way a standings table abbreviates them. */
const STAT_COLUMNS: readonly { key: PlaceholderKey; heading: MessageDescriptor }[] = [
  { key: PlaceholderKey.GAMES_PLAYED, heading: msg`И` },
  { key: PlaceholderKey.GAMES_WON, heading: msg`В` },
  { key: PlaceholderKey.GAMES_DRAWN, heading: msg`Н` },
  { key: PlaceholderKey.GAMES_LOST, heading: msg`П` },
  { key: PlaceholderKey.GOALS_SCORED, heading: msg`ЗМ` },
  { key: PlaceholderKey.GOALS_CONCEDED, heading: msg`ПМ` },
  { key: PlaceholderKey.GOAL_DIFFERENCE, heading: msg`±` },
  { key: PlaceholderKey.GAMES_DIFFERENCE, heading: msg`В−П` },
  { key: PlaceholderKey.POINTS, heading: msg`О` },
];

/** TOURNAMENT_TABLE: the keys sit in the first row, the rows below are decoration. */
const TableScheme: React.FC = () => {
  const { t } = useLingui();
  const text = useKeyText();
  const logo = (
    <Logo round>
      <Shield strokeWidth={1.5} />
    </Logo>
  );
  return (
    <Poster>
      <PosterHeader>
        <Hot k={PlaceholderKey.TOURNAMENT_LOGO}>
          <Logo>
            <Trophy strokeWidth={1.5} />
          </Logo>
        </Hot>
        <Titles>
          <Hot k={PlaceholderKey.CHAMPIONSHIP_NAME}>
            <Title>{text(PlaceholderKey.CHAMPIONSHIP_NAME)}</Title>
          </Hot>
          <Hot k={PlaceholderKey.TOURNAMENT_NAME}>
            <Subtitle>{text(PlaceholderKey.TOURNAMENT_NAME)}</Subtitle>
          </Hot>
        </Titles>
      </PosterHeader>
      <div>
        <Hot k={PlaceholderKey.GROUP_NAME}>{text(PlaceholderKey.GROUP_NAME)}</Hot>
      </div>
      <SchemeTable>
        <thead>
          <tr>
            <th>#</th>
            <th />
            <th className="left">{t`Команда`}</th>
            {STAT_COLUMNS.map(({ key, heading }) => (
              <th key={key}>{t(heading)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <Hot k={PlaceholderKey.TOURNAMENT_POSITION}>{text(PlaceholderKey.TOURNAMENT_POSITION)}</Hot>
            </td>
            <td>
              <Hot k={PlaceholderKey.TEAM_LOGO}>{logo}</Hot>
            </td>
            <td className="left">
              <Hot k={PlaceholderKey.TEAM_NAME}>{text(PlaceholderKey.TEAM_NAME)}</Hot>{' '}
              <Hot k={PlaceholderKey.TEAM_SHORT_NAME}>{text(PlaceholderKey.TEAM_SHORT_NAME)}</Hot>
            </td>
            {STAT_COLUMNS.map(({ key }) => (
              <td key={key}>
                <Hot k={key}>{text(key)}</Hot>
              </td>
            ))}
          </tr>
          {[1, 2].map((row) => (
            <tr key={row}>
              <td>
                <Decor>{row + 1}</Decor>
              </td>
              <td>{logo}</td>
              <td className="left">
                <Decor>{text(PlaceholderKey.TEAM_NAME, row)}</Decor>
              </td>
              {STAT_COLUMNS.map(({ key }) => (
                <td key={key}>
                  <Decor>{text(key, row)}</Decor>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </SchemeTable>
    </Poster>
  );
};

export default TableScheme;
